/** Shared destinations for the brief and workspace knowledge. URLs and saved links stay stable. */
export type KnowledgeSection = "read" | "explore" | "research" | "manage";
export interface KnowledgeDestination {
  id: string;
  label: string;
  to: string;
  more?: boolean;
  admin?: boolean;
}
interface KnowledgeGroup {
  id: KnowledgeSection;
  label: string;
  description: string;
  items: [KnowledgeDestination, ...KnowledgeDestination[]];
}
export const KNOWLEDGE_SECTIONS: [KnowledgeGroup, ...KnowledgeGroup[]] = [
  {
    id: "read",
    label: "Important now",
    description: "Your brief, editions and what happens next",
    items: [
      { id: "brief", label: "Your brief", to: "/briefing" },
      { id: "living-stories", label: "Living stories", to: "/knowledge/stories" },
      { id: "magazine", label: "Magazine", to: "/briefing/magazine", more: true },
      { id: "calendar", label: "Calendar", to: "/briefing/calendar", more: true },
      { id: "futures", label: "Possible futures", to: "/briefing/consequences", more: true },
      { id: "saved", label: "Saved stories", to: "/briefing/saved", more: true },
      { id: "hidden", label: "Hidden stories", to: "/briefing/hidden", more: true },
    ],
  },
  {
    id: "explore",
    label: "Knowledge gallery",
    description: "Important knowledge, its sources and ways to explore it",
    items: [
      { id: "visuals", label: "Knowledge gallery", to: "/knowledge/visuals" },
      { id: "explorer", label: "Knowledge explorer", to: "/knowledge/explore" },
      { id: "subjects", label: "Connected subjects", to: "/briefing/topics", more: true },
      { id: "perspectives", label: "Perspectives", to: "/briefing/perspectives", more: true },
      { id: "browse", label: "Knowledge browser", to: "/knowledge/browse", more: true },
      { id: "tree", label: "Knowledge tree", to: "/knowledge/topdown", more: true },
      { id: "project", label: "Project map", to: "/knowledge/project", more: true },
      { id: "interests", label: "Interests", to: "/briefing/canopy", more: true },
      { id: "timeline", label: "Story tree", to: "/briefing/tree", more: true },
      { id: "space", label: "3D space", to: "/space", more: true },
      { id: "world", label: "World", to: "/world", more: true },
      { id: "aggregate", label: "Knowledge aggregator", to: "/work/aggregate", more: true },
    ],
  },
  {
    id: "research",
    label: "Research & watch",
    description: "Investigate evidence, test ideas and watch for change",
    items: [
      { id: "discoveries", label: "Research discoveries", to: "/knowledge/discoveries" },
      { id: "analysis", label: "Knowledge tools", to: "/knowledge/analysis" },
      { id: "laboratory", label: "Research laboratory", to: "/research", more: true },
      { id: "reasoning", label: "Reasoning", to: "/reasoning", more: true },
      { id: "discover", label: "Workspace reading", to: "/discover", more: true },
      { id: "triggers", label: "Triggers & questions", to: "/knowledge/triggers" },
      { id: "briefings", label: "Forecast history", to: "/knowledge/briefings", more: true },
      { id: "compression", label: "Semantic compression lab", to: "/knowledge/compression" },
    ],
  },
  {
    id: "manage",
    label: "Inputs & health",
    description: "Sources and the health of workspace knowledge",
    items: [
      { id: "channels", label: "Input channels", to: "/settings/channels" },
      { id: "overview", label: "Knowledge health", to: "/knowledge" },
      { id: "diagnostics", label: "Diagnostics", to: "/knowledge/debug", admin: true },
    ],
  },
];

export function knowledgeLocation(pathname: string) {
  if (pathname.startsWith("/knowledge/source/")) pathname = "/knowledge/visuals";
  const matches = KNOWLEDGE_SECTIONS.flatMap((section) =>
    section.items
      .filter((item) => pathname === item.to || pathname.startsWith(`${item.to}/`))
      .map((item) => ({ section, item })),
  ).sort((a, b) => b.item.to.length - a.item.to.length);
  return matches[0] ?? null;
}

/** Retain personal scope between brief views; do not copy personal scope into a workspace page. */
export function knowledgeLinkSearch(to: string, search: Record<string, unknown>) {
  // The hub judges views against the same audience and scope; an item selection is not carried between pages.
  if (to === "/knowledge/stories" || to === "/knowledge/visuals" || to.startsWith("/knowledge/visuals/"))
    return Object.fromEntries(["audience", "ws", "workspaces", "projects", "project", "collection", "scope"]
      .flatMap((key) => search[key] === undefined ? [] : [[key, search[key]]]));
  if (to === "/knowledge/explore" || to.startsWith("/knowledge/analysis"))
    return Object.fromEntries(["ws", "source", "collection", "input", "project", "days", "entity", "lens", "q", "type", "basis", "method",
      ...(to === "/knowledge/explore" ? ["audience", "scope", "workspaces", "projects", "subject"] : [])]
      .flatMap((key) => search[key] === undefined ? [] : [[key, search[key]]]));
  if (!to.startsWith("/briefing")) return search.ws === undefined ? {} : { ws: search.ws };
  return Object.fromEntries(
    ["ws", "scope", "workspaces", "projects", "subject", "days"].flatMap((key) =>
      search[key] === undefined ? [] : [[key, search[key]]],
    ),
  );
}
